import { useEffect, useState } from "react";
import { X, Sparkles, TrendingUp, Shield } from "lucide-react";

const DISMISS_KEY = "cauldron_coin_banner_dismissed";

/**
 * Full-width promotional banner for the Cauldron Reward Token (CAUL). Sits between the
 * top nav and the post composer on the feed page. Dismissible — preference
 * persists in localStorage for 24h.
 */
export default function CauldronCoinBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts || Date.now() - ts > 24 * 60 * 60 * 1000) setVisible(true);
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <section
      className="relative overflow-hidden parchment ember-glow border-secondary/40"
      data-testid="cauldron-coin-banner"
      style={{
        background:
          "radial-gradient(ellipse 60% 80% at 20% 50%, rgba(212,181,140,0.18), transparent 60%), radial-gradient(ellipse 50% 60% at 90% 50%, rgba(182,163,211,0.18), transparent 60%), linear-gradient(135deg, #1E1638 0%, #2A1F4A 60%, #15102A 100%)",
      }}
    >
      <button
        onClick={dismiss}
        aria-label="Dismiss banner"
        className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-white/5 hover:bg-white/15 text-foreground/70 hover:text-foreground transition-colors"
        data-testid="banner-dismiss-btn"
      >
        <X size={14} strokeWidth={2} />
      </button>

      <div className="relative grid grid-cols-1 md:grid-cols-[1fr_auto] gap-6 px-6 py-8 sm:px-10 sm:py-10 items-center">
        {/* Decorative circles */}
        <div className="absolute -left-12 -bottom-12 w-44 h-44 rounded-full bg-secondary/20 blur-2xl pointer-events-none" />
        <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-primary/15 blur-3xl pointer-events-none" />

        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <span className="label-mystic text-primary inline-flex items-center gap-1.5">
              <Sparkles size={12} strokeWidth={1.5} /> Featured
            </span>
            <span className="label-mystic text-foreground/60">Cauldron Reward Token · $CAUL</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl text-foreground leading-tight">
            The reward token of the <span className="text-primary">Cauldron</span>.
          </h2>
          <p className="text-foreground/80 mt-3 max-w-xl text-sm sm:text-base">
            $CAUL powers every ritual, reading, and referral on the platform — and the supply is finite.
            Hold, transact, and earn rewards across the spiritual marketplace.
          </p>

          <div className="flex flex-wrap items-center gap-4 mt-5">
            <div className="flex items-center gap-2 text-xs text-foreground/75">
              <TrendingUp size={14} strokeWidth={1.5} className="text-secondary" />
              Earn 5% referral commission
            </div>
            <div className="flex items-center gap-2 text-xs text-foreground/75">
              <Shield size={14} strokeWidth={1.5} className="text-secondary" />
              Fixed supply · Provider rewards
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-6">
            <a
              href="/wallet"
              className="bg-primary text-primary-foreground px-6 py-2.5 rounded-sm uppercase tracking-wider text-xs font-semibold hover:bg-primary/90"
              data-testid="banner-cta-buy"
            >
              View My Wallet →
            </a>
            <a
              href="/cashout"
              className="border border-primary/60 text-primary px-6 py-2.5 rounded-sm uppercase tracking-wider text-xs hover:bg-primary/10"
              data-testid="banner-cta-cashout"
            >
              Cash Out
            </a>
            <a
              href="/referrals"
              className="border border-secondary/60 text-secondary px-6 py-2.5 rounded-sm uppercase tracking-wider text-xs hover:bg-secondary/10"
              data-testid="banner-cta-earn"
            >
              Earn CAUL
            </a>
          </div>
          <p className="text-[10px] text-foreground/40 mt-4 uppercase tracking-wider">
            ⓘ Not financial advice. CAUL is a platform reward token. Crypto investments carry risk.
          </p>
        </div>

        {/* Coin visual */}
        <div className="relative hidden md:flex items-center justify-center w-56">
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/40 to-secondary/20 blur-2xl" />
          <div className="relative w-44 h-44 rounded-full bg-gradient-to-br from-primary via-primary/90 to-primary/60 border-4 border-primary/30 shadow-2xl flex items-center justify-center"
               style={{ boxShadow: "0 0 60px rgba(212,181,140,0.45), inset 0 4px 12px rgba(255,255,255,0.18), inset 0 -8px 18px rgba(0,0,0,0.25)" }}>
            <div className="text-center">
              <div className="font-serif text-6xl text-primary-foreground" style={{ textShadow: "0 2px 6px rgba(0,0,0,0.3)" }}>⚱</div>
              <div className="font-serif text-xl text-primary-foreground tracking-widest mt-1">CAUL</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
